import React from 'react';
import type { LucideIcon } from 'lucide-react';

export interface FolderTabItem {
  value: string;
  label: string;
  icon?: LucideIcon;
  count?: number;
  tone?: 'default' | 'red' | 'orange' | 'yellow' | 'green';
}

interface FolderTabNavProps {
  tabs: FolderTabItem[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
}

const TONE_BADGE: Record<string, string> = {
  red: 'bg-red-500/20 text-red-300',
  orange: 'bg-orange-500/20 text-orange-300',
  green: 'bg-green-500/20 text-green-300',
  yellow: 'bg-yellow-500/20 text-yellow-300',
  default: 'bg-yellow-500/20 text-yellow-300',
};

/**
 * FolderTabNav
 *
 * A horizontal "main menu" tab strip meant to visually read as folder tabs
 * sitting above a page/panel: the active tab is taller, merges seamlessly
 * into the content pane below it (matching background, no bottom border,
 * -mb-px overlap), while inactive tabs sit shorter and visibly "behind" it.
 * A springy cubic-bezier transition on activation gives the lift a bit of
 * bounce, and the icon does a quick pop-in spin the moment its tab becomes
 * active. Pair with FolderTabLayout for the full tab-row + content-pane shell.
 */
const FolderTabNav: React.FC<FolderTabNavProps> = ({ tabs, value, onValueChange, className }) => (
  <div className={`flex flex-wrap items-end gap-1 ${className ?? ''}`}>
    {tabs.map((tab) => {
      const Icon = tab.icon;
      const isActive = value === tab.value;
      return (
        <button
          key={tab.value}
          type="button"
          onClick={() => onValueChange(tab.value)}
          aria-pressed={isActive}
          className={`relative flex max-w-[140px] items-center gap-1.5 rounded-t-lg border px-2.5 py-1.5 text-xs font-semibold transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
            isActive
              ? 'z-10 min-h-[44px] -mb-px -translate-y-0.5 border-yellow-600/50 border-b-zinc-900 bg-zinc-900 text-yellow-300 shadow-[0_-6px_14px_rgba(0,0,0,0.25)]'
              : 'min-h-[36px] translate-y-0 border-yellow-900/20 border-b-yellow-900/20 bg-zinc-950/70 text-gray-400 hover:-translate-y-px hover:bg-zinc-900/60 hover:text-yellow-300'
          }`}
        >
          {Icon && (
            <Icon
              key={isActive ? `${tab.value}-active` : `${tab.value}-inactive`}
              className={`h-4 w-4 shrink-0 ${isActive ? 'animate-tab-icon-pop' : ''}`}
            />
          )}
          <span className="text-left leading-tight">{tab.label}</span>
          {typeof tab.count === 'number' && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold shrink-0 ${
                isActive ? 'bg-black/20 text-yellow-200' : TONE_BADGE[tab.tone ?? 'default']
              }`}
            >
              {tab.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export default FolderTabNav;
